import React, { useEffect, useState } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActionArea,
  CardMedia,
  CircularProgress,
  Box,
  Tooltip,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from 'react-hot-toast';
import ImageCarousel from "components/ImageCarousal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarXmark } from "@fortawesome/free-solid-svg-icons";

// Optional: Reuse the same icons
const TimeIcon = () => (


  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const defaultImage =
  "https://ismaana.org/wp-content/uploads/2023/02/naresh-center-02.jpg";

const StudentEvents = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const options = { year: 'numeric', month: '2-digit', day: '2-digit' };



  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        "https://api.nvcti.in/events/getAllEvents",
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.data.success) throw new Error(res.data.message);
      const formatted = res.data.data.map((event) => ({
        id: event.event_id,
        name: event.event_name,
        description: event.description,
        startDate: new Date(event.start_date).toLocaleDateString('en-US', options),
        endDate: new Date(event.end_date).toLocaleDateString('en-US', options),
        image: event.banner || defaultImage,
        event_images: event.event_images,
      }));

      setEvents(formatted);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleClick = (id, endDate, event) => {
    const curDate = new Date().toLocaleDateString('en-US', options);
    if (curDate > endDate) {
      toast.error("The Registration for this Event is Closed.")
    } else {
      navigate(`/student/student-event-registration`, { state: { type: "create", id, formValues: null, event } });
    }
  };


  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Toaster position="top-left" reverseOrder={false} />
      <div className="relative w-full">
        <div className="flex justify-between items-center px-4 mt-6">
          <p className="font-semibold text-lg text-white">Events</p>
        </div>

        <div className="px-6 mt-4 mb-6">
          <form className="flex flex-row flex-wrap items-center">
            <div className="relative flex w-full flex-wrap items-stretch">
              <span className="z-10 h-full leading-snug font-normal absolute text-center text-blueGray-900 bg-transparent rounded text-base items-center justify-center w-8 pl-3 py-3">
                <i className="fas fa-search"></i>
              </span>
              <Tooltip title="Search Event" arrow>
                <input
                  type="text"
                  placeholder="Search by Event Name"
                  className="border-0 px-3 py-3 placeholder-blueGray-300 text-blueGray-600 relative bg-white rounded text-sm shadow outline-none focus:outline-none focus:ring w-full pl-10 pr-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Tooltip>
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-0 top-0 bottom-0 flex items-center justify-center w-10 text-blueGray-900 hover:text-blueGray-600 focus:outline-none"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>
          </form>
        </div>

        {(() => {
          const filteredEvents = events.filter((e) =>
            e.name.toLowerCase().includes(searchTerm.toLowerCase())
          );

          if (filteredEvents.length === 0) {
            return (
              <div className="flex flex-col items-center justify-center w-full min-h-[400px] text-blueGray-500">
                <div className="bg-white p-8 rounded-full shadow-lg mb-6 flex items-center justify-center w-32 h-32">
                  <FontAwesomeIcon icon={faCalendarXmark} className="text-5xl text-blueGray-300" />
                </div>
                <h3 className="text-2xl font-semibold mb-2 text-blueGray-700">No Upcoming Events</h3>
                <p className="text-lg text-blueGray-400 max-w-md text-center">
                  It looks like there are no events scheduled at the moment. Please check back later!
                </p>
              </div>
            );
          }

          return (
            <Grid container spacing={4}>
              {filteredEvents.map((event) => (
                <Grid item xs={12} sm={6} md={4} key={event.id}>
                  <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                    <CardActionArea>
                      <ImageCarousel images={event.event_images} />
                      <CardContent onClick={() => handleClick(event.id, event.endDate, event)}>
                        <Typography
                          gutterBottom
                          variant="h6"
                          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                        >
                          {event.name}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 0.5 }}
                        >
                          <TimeIcon />
                          <Typography variant="body2" color="text.secondary">
                            {event.startDate} - {event.endDate}
                          </Typography>
                        </Stack>

                        <Typography variant="body2" color="text.secondary">
                          {event.description.length > 100
                            ? `${event.description.substring(0, 100)}...`
                            : event.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          );
        })()}
      </div>
    </>
  );
};

export default StudentEvents;
